import { Component, OnInit, OnDestroy, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ButtonModule } from 'primeng/button';
import { SelectModule } from 'primeng/select';
import { MessageService } from 'primeng/api';
import { TipoProductoService } from '../../../services/tipo-producto/tipo-producto.service';
import { TipoProducto } from '../../../domain/tipo-producto.domain';

@Component({
  selector: 'app-tipo-producto-edit',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    ToggleSwitchModule,
    SelectModule,
    ButtonModule
  ],
  templateUrl: './tipo-producto-edit.component.html',
  styleUrls: ['./tipo-producto-edit.component.scss']
})
export class TipoProductoEditComponent implements OnInit, OnDestroy {
  tipoProductoForm!: FormGroup;
  loading = signal<boolean>(false);
  isEditMode = signal<boolean>(false);
  tipoProductoId: string | null = null;
  tipoProducto = signal<TipoProducto | null>(null);

  private destroy$ = new Subject<void>();

  private tipoProductoService = inject(TipoProductoService);

  canalOptions = [
    { label: 'Aire', value: 'AIRE' },
    { label: 'Web', value: 'WEB' },
    { label: 'Redes', value: 'REDES' },
    { label: 'Editorial', value: 'EDITORIAL' },
    { label: 'Evento', value: 'EVENTO' }
  ];

  unidadOptions = [
    { label: 'Segundo', value: 'SEGUNDO' },
    { label: 'Día', value: 'DIA' },
    { label: 'Pieza', value: 'PIEZA' },
    { label: 'Paquete', value: 'PAQUETE' }
  ];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private messageService: MessageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.setupRouteParamListener();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupRouteParamListener(): void {
    this.route.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        const newId = params.get('id');
        if (newId !== this.tipoProductoId) {
          this.loadTipoProducto();
        }
      });
  }

  initForm(): void {
    this.tipoProductoForm = this.fb.group({
      codigo: ['', Validators.required],
      nombre: ['', Validators.required],
      canal: [null, Validators.required],
      unidadTarifacion: [null, Validators.required],
      requierePrograma: [false],
      requiereMaterial: [false],
      requiereFranjaHoraria: [false],
      requiereDiaTipo: [false],
      requiereFormatoDigital: [false],
      requiereUbicacionDigital: [false],
      permiteBonificacion: [false],
      active: [true]
    });
  }

  loadTipoProducto(): void {
    const newTipoProductoId = this.route.snapshot.paramMap.get('id');

    if (newTipoProductoId !== this.tipoProductoId) {
      this.tipoProductoForm.reset();
      this.tipoProducto.set(null);
    }

    this.tipoProductoId = newTipoProductoId;

    if (this.tipoProductoId && this.tipoProductoId !== 'nuevo') {
      this.isEditMode.set(true);
      this.loading.set(true);

      this.tipoProductoService.getById(this.tipoProductoId!).pipe(
        takeUntil(this.destroy$)
      ).subscribe({
        next: (tipoProducto: TipoProducto) => {
          if (tipoProducto) {
            this.tipoProducto.set(tipoProducto);
            this.tipoProductoForm.patchValue({
              codigo: tipoProducto.codigo || '',
              nombre: tipoProducto.nombre || '',
              canal: tipoProducto.canal || null,
              unidadTarifacion: tipoProducto.unidadTarifacion || null,
              requierePrograma: tipoProducto.requierePrograma ?? false,
              requiereMaterial: tipoProducto.requiereMaterial ?? false,
              requiereFranjaHoraria: tipoProducto.requiereFranjaHoraria ?? false,
              requiereDiaTipo: tipoProducto.requiereDiaTipo ?? false,
              requiereFormatoDigital: tipoProducto.requiereFormatoDigital ?? false,
              requiereUbicacionDigital: tipoProducto.requiereUbicacionDigital ?? false,
              permiteBonificacion: tipoProducto.permiteBonificacion ?? false,
              active: tipoProducto.active ?? true
            });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Tipo de producto no encontrado',
              life: 3000
            });
            this.router.navigate(['/tipo-productos']);
          }
          this.loading.set(false);
        },
        error: (error) => {
          this.loading.set(false);
          console.error('Error al cargar tipo de producto:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar los datos del tipo de producto',
            life: 3000
          });
        }
      });
    } else {
      this.isEditMode.set(false);
    }
  }

  onSubmit(): void {
    if (this.tipoProductoForm.invalid) {
      this.markFormGroupTouched(this.tipoProductoForm);
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Inválido',
        detail: 'Por favor complete todos los campos requeridos',
        life: 3000
      });
      return;
    }

    const formValue = this.tipoProductoForm.value;

    const tipoProducto: TipoProducto = {
      codigo: formValue.codigo,
      nombre: formValue.nombre,
      canal: formValue.canal,
      unidadTarifacion: formValue.unidadTarifacion,
      requierePrograma: formValue.requierePrograma ?? false,
      requiereMaterial: formValue.requiereMaterial ?? false,
      requiereFranjaHoraria: formValue.requiereFranjaHoraria ?? false,
      requiereDiaTipo: formValue.requiereDiaTipo ?? false,
      requiereFormatoDigital: formValue.requiereFormatoDigital ?? false,
      requiereUbicacionDigital: formValue.requiereUbicacionDigital ?? false,
      permiteBonificacion: formValue.permiteBonificacion ?? false,
      active: formValue.active ?? true
    };

    if (this.isEditMode()) {
      tipoProducto.id = this.tipoProductoId || undefined;
    }

    this.loading.set(true);

    const operation = this.isEditMode()
      ? this.tipoProductoService.update(this.tipoProductoId!, tipoProducto)
      : this.tipoProductoService.create(tipoProducto);

    operation.subscribe({
      next: (response) => {
        this.loading.set(false);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: this.isEditMode() ? 'Tipo de producto actualizado correctamente' : 'Tipo de producto creado correctamente',
          life: 3000
        });
        setTimeout(() => {
          this.router.navigate(['/tipo-productos']);
        }, 1500);
      },
      error: (error) => {
        this.loading.set(false);
        console.error('Error al guardar tipo de producto:', error);

        let errorMessage = 'Error al guardar el tipo de producto';

        if (error.status === 422 && error.error?.violations) {
          const violations = error.error.violations;
          if (violations.length === 1) {
            errorMessage = violations[0].message;
          } else {
            errorMessage = `Se encontraron ${violations.length} errores de validación`;
          }
        } else if (error.error?.detail) {
          errorMessage = error.error.detail;
        } else if (error.error?.message) {
          errorMessage = error.error.message;
        }

        this.messageService.add({
          severity: 'error',
          summary: 'Error de Validación',
          detail: errorMessage,
          life: 5000
        });
      }
    });
  }

  onCancel(): void {
    this.router.navigate(['/tipo-productos']);
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  get pageTitle(): string {
    return this.isEditMode() ? 'Editar Tipo de Producto' : 'Nuevo Tipo de Producto';
  }
}
